import React, { useState, useEffect, useCallback } from 'react';
import { Plus, AlertCircle, Archive } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Modal from '../shared/Modal';
import MoveToWorkshopModal from '../shared/MoveToWorkshopModal';
import ArchivedSessionsPanel from './ArchivedSessionsPanel';
import SessionCard from '../dashboard/SessionCard';
import { api } from '../../api/client';

// Foresight Sessions tab within a single Workshop.
const WorkshopForesightTab = ({ workshop, onNavigateToSession }) => {
  const { currentUser, setActiveSession, setUncertainties, setVotes } = useAppContext();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  const [movingSession, setMovingSession] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const canCreate = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  const loadSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getSessions(workshop.id);
      setSessions(data);
    } catch (error) {
      console.error('Failed to load foresight sessions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workshop.id]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const createSession = async () => {
    if (!newSessionName) return;
    try {
      const newSession = await api.createSession({
        name: newSessionName,
        description: newSessionDescription,
        workshop_id: workshop.id,
      });
      setSessions([...sessions, newSession]);
      setShowCreateModal(false);
      setNewSessionName('');
      setNewSessionDescription('');
    } catch (error) {
      alert('Failed to create session: ' + error.message);
    }
  };

  const handleSessionClick = async (session) => {
    setActiveSession(session);
    try {
      const uncertaintiesData = await api.getUncertainties(session.id);
      const votesData = await api.getVotes(session.id);
      setUncertainties(uncertaintiesData);
      setVotes(votesData);
      onNavigateToSession();
    } catch (error) {
      alert('Failed to load session: ' + error.message);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await api.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (error) {
      alert('Failed to delete session: ' + error.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Foresight Sessions</h2>
        <div className="flex gap-2">
          {currentUser.role === 'admin' && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center space-x-2 border px-4 py-2 rounded-lg ${
                showArchived ? 'bg-[#01006A] text-white border-[#01006A]' : 'text-gray-600 border-gray-300 hover:border-[#01006A] hover:text-[#01006A]'
              }`}
            >
              <Archive className="w-4 h-4" />
              <span>{showArchived ? 'Hide Archived' : 'Show Archived'}</span>
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]"
            >
              <Plus className="w-5 h-5" />
              <span>New Session</span>
            </button>
          )}
        </div>
      </div>

      {showArchived && (
        <div className="mb-6">
          <ArchivedSessionsPanel
            workshopId={workshop.id}
            fetchArchived={api.getArchivedSessions}
            onRestore={api.restoreSession}
            onPurge={api.purgeSession}
            itemLabel="session"
          />
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map(session => (
            <SessionCard
              key={session.id}
              session={session}
              onClick={() => handleSessionClick(session)}
              onDelete={handleDeleteSession}
              onMove={(s) => setMovingSession(s)}
              currentUser={currentUser}
            />
          ))}

          {sessions.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No foresight sessions in this workshop yet.</p>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Foresight Session"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Name</label>
            <input
              type="text"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="Q1 2025 Strategy Review"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={newSessionDescription}
              onChange={(e) => setNewSessionDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              rows="3"
              placeholder="Exploring key uncertainties for strategic planning..."
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={createSession}
              disabled={!newSessionName}
              className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
            >
              Create
            </button>
            <button
              onClick={() => setShowCreateModal(false)}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {movingSession && (
        <MoveToWorkshopModal
          itemName={movingSession.name}
          currentWorkshopId={workshop.id}
          onMove={(targetWorkshopId) => api.moveSession(movingSession.id, targetWorkshopId)}
          onMoved={() => setSessions(sessions.filter(s => s.id !== movingSession.id))}
          onClose={() => setMovingSession(null)}
        />
      )}
    </div>
  );
};

export default WorkshopForesightTab;
